import { jsPDF as JSPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

function getPeerReviewData (project, review) {
  // Extract reviewers with role "Peer Reviewer" from project.shareWithIds
  const reviewers = project.sharedWithIds
    .filter(r => r.role === 'Peer Reviewer' && r.name)
    .map(r => r.name);

  // Define the keys and titles you want to include in the sections
  const sectionKeys = [
    { key: 'lapseSkewnessDistribution', title: 'Lapse Skewness Distribution of Day of Lapse Since Anniversary' },
    { key: 'lapseSkewness', title: 'Lapse Skewness -- Monthly Skewness' },
    { key: 'columnMappingComment', title: 'Column Mapping' },
    { key: 'valueMappingComment', title: 'Value Mapping' },
    { key: 'piiComment', title: 'Personally Identifiable Information (PII)' },
    { key: 'lapseExclusion', title: 'Lapse Exclusions' },
    { key: 'exposureRiskHistory', title: 'Exposure vs Risk History' },
    { key: 'duplicates', title: 'Duplicates' }
  ];

  const sections = sectionKeys.map(({ key, title }) => {
    const obj = review?.[key] || {};
    const section = {
      title: obj.title || title,
      comment: obj.comment !== undefined ? obj.comment : 'None'
    };
    // Special handling for PII and Duplicates
    if (key === 'piiComment') {
      // Set yes/no based on piiReview value
      section.pii = {
        yes: obj.piiReview === 'YES',
        no: obj.piiReview === 'NO'
      };
    }
    if (key === 'duplicates' && typeof obj.sumDuplicates === 'number') {
      section.extra = `Based on the user's latest mappings we found ${obj.sumDuplicates} duplicates.`;
    }
    return section;
  });

  return {
    reviewers,
    status: 'Pending',
    sections
  };
}

/**
 * Build column mapping data for the report from project and mapping Redux slices.
 * Each row: { file, common, source }
 */
function getColumnMappingData (project, mapping) {
  const mappingByGuid = {};
  mapping.mappings.forEach(m => {
    if (m.guid) mappingByGuid[m.guid] = m;
  });

  // Build array of guids for files that are successfully uploaded
  const successfullyUploadedFiles = Array.isArray(project.files)
    ? project.files
      .filter(f => f.profileStatus === 'SUCCEEDED' && f.uploaded === true && f.guid)
      .map(f => f.guid)
    : [];

  const rows = [];
  const files = Array.isArray(project.files) ? project.files : [];
  files.forEach(file => {
    const fileName = file.fileName || file.path || '';
    const guid = file.guid;
    // Only process files whose guid is in successfullyUploadedFiles
    if (!successfullyUploadedFiles.includes(guid)) return;
    const fileMapping = mappingByGuid[guid];
    if (fileMapping && Array.isArray(fileMapping.columns)) {
      fileMapping.columns.forEach(col => {
        rows.push({
          file: fileName,
          common: col.target || '',
          source: col.source || ''
        });
      });
    }
  });

  return rows;
}

/**
 * Build value mapping data for the report from project and mapping Redux slices.
 * Each row: { file, column, newValue, oldValue }
 * Only include columns where source === "Unmapped"
 */
function getValueMappingData (project, mapping) {
  if (
    !project ||
    !project.files ||
    !Array.isArray(project.files) ||
    !mapping ||
    !Array.isArray(mapping.mappings)
  ) {
    return [];
  }

  // Build a lookup for mapping.mappings by guid for fast access
  const mappingByGuid = {};
  mapping.mappings.forEach((m) => {
    if (m.guid) mappingByGuid[m.guid] = m;
  });

  // Build array of guids for files that are successfully uploaded
  const successfullyUploadedFiles = project.files
    .filter(f => f.profileStatus === 'SUCCEEDED' && f.uploaded === true && f.guid)
    .map(f => f.guid);

  const rows = [];
  project.files.forEach((file) => {
    const fileName = file.fileName || file.path || '';
    const guid = file.guid;
    // Only process files whose guid is in successfully_uploaded_files
    if (!successfullyUploadedFiles.includes(guid)) return;
    const fileMapping = mappingByGuid[guid];
    if (fileMapping && Array.isArray(fileMapping.columns)) {
      fileMapping.columns.forEach((col) => {
        if (col.source === 'Unmapped' && Array.isArray(col.valMapp)) {
          col.valMapp.forEach((vm) => {
            // Extract value from "value" property (prefer vm.singleValue.value, then vm.value)
            let value = '';
            if (vm.singleValue && typeof vm.singleValue.value !== 'undefined' && vm.singleValue.value !== null) {
              value = vm.singleValue.value;
            } else if (typeof vm.value !== 'undefined' && vm.value !== null) {
              value = vm.value;
            }
            rows.push({
              file: fileName,
              column: col.target || '',
              newValue: value,
              oldValue: 'Unmapped'
            });
          });
        }
      });
    }
  });

  return rows;
}

function getPreCalculationTableData (preCalc) {
  const arr = preCalc.preCalculationCheckData;

  return arr.map(item => {
    let statusText = '';
    let acceptedText = '';
    if (item.status === 0) {
      statusText = 'Succeeded';
      acceptedText = 'No issue';
    } else if (item.status === 1) {
      statusText = 'Failed';
      acceptedText = 'Fixed/Acknowledged';
    } else {
      statusText = 'Unknown';
      acceptedText = '';
    }
    return {
      checkName: item.label || item.checkName || '',
      status: statusText,
      accepted: acceptedText
    };
  });
}

// Helper to extract model setup parameters from project and calculation redux slices
function getModelSetupParameters (project, calculation) {
  const officialJobId = project.officialJobId;
  const job = calculation.jobHistory.find(j => j.JobId === officialJobId);

  const treatyRestrictions = job?.TreatyRestrictions ?? '';
  const policyRestrictions = job?.PolicyRestrictions ?? '';

  return [
    { label: 'Observation Date', value: job?.ObservationDate || null },
    { label: 'Study Period Start', value: job?.PeriodStartDate || null },
    { label: 'Study Period End', value: job?.PeriodEndDate || null },
    { label: 'Treaty Restrictions', value: treatyRestrictions || 'None' },
    { label: 'Policy Restrictions', value: policyRestrictions || 'None' }
  ];
}

// Helper to extract aggregators string from calculation/jobHistory
function getAggregators (project, calculation) {
  const officialJobId = project.officialJobId;
  const job = calculation.jobHistory.find(j => j.JobId === officialJobId);
  const aggs = job?.Aggregators ? job.Aggregators : 'None';
  return `Aggregators Selected: ${aggs}`;
}

// Helper to extract applications array from calculation/jobHistory
function getApplications (project, calculation) {
  const officialJobId = project.officialJobId;
  const job = calculation.jobHistory.find(j => j.JobId === officialJobId) || {};

  return [
    { name: 'Apply Per Thousand Incidence Rate Cap', selected: job.ApplyIncidenceRateCap === 1 ? 'Yes' : 'No' },
    { name: 'Apply Monthly Aggregation', selected: job.AddMonthlyAggregation === 1 ? 'Yes' : 'No' },
    { name: 'Apply Substandard Flat Extra', selected: job.ApplySubstandardFlatExtra === 1 ? 'Yes' : 'No' },
    { name: 'Apply Buhlmann Constants', selected: job.AddBuhlmannConstants === 1 ? 'Yes' : 'No' },
    { name: 'Apply Expected Lapse Rates', selected: job.ApplyExpectedLapseRates === 1 ? 'Yes' : 'No' },
    { name: 'Include Warnings', selected: job.IncludeWarnings === 1 ? 'Yes' : 'No' },
    { name: 'Perform Monte Carlo', selected: job.PerformMonteCarlo === 1 ? 'Yes' : 'No' },
    { name: 'Calculate IBNR', selected: job.CalculateIbnr === 1 ? 'Yes' : 'No' }
  ];
}

function getExpectedBasis (project, calculation, refDataBasis) {
  const officialJobId = project.officialJobId;
  const job = calculation.jobHistory.find(j => j.JobId === officialJobId);
  const basisValue = job?.Basis;
  let basisObj = null;
  if (Array.isArray(refDataBasis)) {
    basisObj = refDataBasis.find(b => String(b.caseNumber) === String(basisValue));
  } else if (refDataBasis && String(refDataBasis.caseNumber) === String(basisValue)) {
    basisObj = refDataBasis;
  }
  return basisObj
    ? `Bases Selected: ${basisObj.name}`
    : 'Bases Selected: Unknown';
}

// Helper to build All Policies Imported table data from postCalc.policyTrackerData
function getAllPoliciesImportedData (postCalc) {
  const table = postCalc.policyTrackerData;
  const totalCount = table[0]?.policy_count || 0;
  return table.map((row, idx) => ({
    policy_status: idx === 0 ? '' : row.policy_status,
    policy_count: row.policy_count,
    percent: idx === 0
      ? 'None'
      : totalCount
        ? `${((row.policy_count / totalCount) * 100).toFixed(2)}%`
        : '0%'
  }));
}

// Helper to convert image to base64 (for browser)
async function getImageBase64 (url) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.crossOrigin = 'Anonymous';
    img.onload = function () {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = reject;
    img.src = url;
  });
}

async function generateAuditReport (
  project,
  mapping,
  preCalc,
  calculation,
  refDataBasis,
  postCalc,
  review,
  dataPrepLog
) {
  const doc = new JSPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const headerImgWidth = 60;
  const headerImgHeight = 24;
  const headerImgX = pageWidth - 170;
  const headerImgY = 24;
  const headerTextX = headerImgX + headerImgWidth + 8; // 8pt gap after image
  const headerTextY = headerImgY + headerImgHeight / 2 + 4; // Vertically center text with image

  // Check if dataPrepLog.Items exists and is a non-empty array
  let hasDataPrep = false;
  const dataPrepRows = [];
  if (dataPrepLog && Array.isArray(dataPrepLog.Items) && dataPrepLog.Items.length > 0) {
    hasDataPrep = true;
    // Loop through Items and build rows
    for (let i = 0; i < dataPrepLog.Items.length; i++) {
      const item = dataPrepLog.Items[i];
      dataPrepRows.push([
        item.eventType || '',
        item.createdDate || '',
        item.message || '',
        item.sql || ''
      ]);
    }
  }

  const totalPages = hasDataPrep ? 9 : 8;
  let pageNum = 1;
  let y = 40;

  // Load logo
  let logoBase64 = '';
  try {
    logoBase64 = await getImageBase64('/Redi_portal.png');
  } catch (e) {}

  // --- HEADER DRAW FUNCTION ---
  function drawHeader () {
    if (logoBase64) {
      doc.addImage(logoBase64, 'PNG', headerImgX, headerImgY, headerImgWidth, headerImgHeight);
      doc.setFontSize(12);
      doc.text(`| Project: ${project.id}`, headerTextX, headerTextY, { align: 'left', baseline: 'middle' });
    }
  }

  // --- 1. Cover Page ---
  drawHeader();

  // Centered main logo with preserved aspect ratio
  if (logoBase64) {
    // Create a temporary image to get natural width/height
    const img = new window.Image();
    img.src = logoBase64;
    // Default size if image not loaded yet
    let drawWidth = 240;
    let drawHeight = 100;
    // If image is loaded, use its aspect ratio
    if (img.width && img.height) {
      const maxWidth = 240;
      const maxHeight = 100;
      const ratio = Math.min(maxWidth / img.width, maxHeight / img.height);
      drawWidth = img.width * ratio;
      drawHeight = img.height * ratio;
    }
    doc.addImage(
      logoBase64,
      'PNG',
      pageWidth / 2 - drawWidth / 2,
      200,
      drawWidth,
      drawHeight
    );
  }

  y = 370;
  doc.setFontSize(20); // Increase the font size for the title
  doc.setFont(undefined, 'bold');
  doc.text(`AUDIT REPORT FOR PROJECT ${project.id}`, pageWidth / 2, y, { align: 'center' });
  doc.setFont(undefined, 'normal'); // Reset to normal for following text
  y += 32;

  doc.setFontSize(12);
  doc.text(
    `This report highlights the key items in REDi Project ID: ${project.id}.`,
    pageWidth / 2,
    y,
    { align: 'center' }
  );
  y += 20;
  doc.text(
    'This report is generated based off the most recent RED engine output.',
    pageWidth / 2,
    y,
    { align: 'center' }
  );

  // Footer
  doc.setFontSize(10);
  doc.text(`Page 1 of ${totalPages}`, pageWidth - 80, 820);
  doc.addPage(); pageNum++;

  // --- 2. Upload Summary ---
  drawHeader();
  doc.setFontSize(18);
  doc.setFont(undefined, 'bold');
  doc.text('UPLOAD SUMMARY', 40, 50);
  doc.setFont(undefined, 'normal');
  doc.setFontSize(12);
  let y2 = 80;
  doc.text('Project Id: ' + (project.id || ''), 40, y2); y2 += 18;
  doc.text('Project Name: ' + (project.projectName || ''), 40, y2); y2 += 18;
  doc.text('Project Date: ' + (project.projectDate || ''), 40, y2); y2 += 18;
  doc.text('Client: ' + (project.clientName || ''), 40, y2); y2 += 18;
  doc.text('Countries: ' + (project.country || ''), 40, y2); y2 += 18;
  doc.text('Products: ' + (project.product || ''), 40, y2); y2 += 18;
  doc.text('Files:', 40, y2);
  if (project.files) {
    project.files.forEach((f, i) => {
      doc.text('- ' + f.fileName, 40, y2 + 16 * (i + 1));
    });
    y2 += 16 * (project.files.length + 1);
  }
  doc.setFontSize(10);
  doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth - 80, 820);
  doc.addPage(); pageNum++;

  // --- Data Prep Page (if any logs) ---
  if (hasDataPrep) {
    drawHeader();
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.text('DATA PREP', 40, 50);
    doc.setFont(undefined, 'normal');
    doc.setFontSize(12);

    // Table: Type, Run date, Message, SQL
    autoTable(doc, {
      startY: 80,
      head: [['Type', 'Run date', 'Message', 'SQL']],
      body: dataPrepRows,
      theme: 'grid',
      headStyles: { fillColor: [220, 220, 220] },
      styles: { fontSize: 10, cellWidth: 'wrap' },
      columnStyles: {
        0: { cellWidth: 70 }, // Type
        1: { cellWidth: 110 }, // Run date
        2: { cellWidth: 120 }, // Message
        3: { cellWidth: 180 } // SQL
      }
    });

    doc.setFontSize(10);
    doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth - 80, 820);
    doc.addPage(); pageNum++;
  }

  // --- 5. Column Mapping ---
  drawHeader();
  doc.setFontSize(18);
  doc.setFont(undefined, 'bold');
  doc.text('COLUMN MAPPINGS', 40, 50);
  doc.setFont(undefined, 'normal');
  const colMap = getColumnMappingData(project, mapping);
  autoTable(doc, {
    startY: 70,
    head: [['File', 'Common', 'Source']],
    body: colMap.map(row => [row.file, row.common, row.source]),
    theme: 'grid',
    headStyles: { fillColor: [220, 220, 220] },
    styles: { fontSize: 10 }
  });
  doc.setFontSize(10);
  doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth - 80, 820);
  doc.addPage(); pageNum++;

  // --- 6. Value Mapping ---
  drawHeader();
  doc.setFontSize(18);
  doc.setFont(undefined, 'bold');
  doc.text('VALUE MAPPINGS', 40, 50);
  doc.setFont(undefined, 'normal');
  const valMap = getValueMappingData(project, mapping);
  autoTable(doc, {
    startY: 70,
    head: [['File', 'Column', 'New Value', 'Old Value']],
    body: valMap.map(row => [row.file, row.column, row.newValue, row.oldValue]),
    theme: 'grid',
    headStyles: { fillColor: [220, 220, 220] },
    styles: { fontSize: 10 }
  });
  doc.setFontSize(10);
  doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth - 80, 820);
  doc.addPage(); pageNum++;

  // --- 7. Pre-Calculation Checks ---
  drawHeader();
  doc.setFontSize(18);
  doc.setFont(undefined, 'bold');
  doc.text('PRE-CALCULATION CHECKS', 40, 50);
  doc.setFont(undefined, 'normal');
  const preCalcTable = getPreCalculationTableData(preCalc);
  autoTable(doc, {
    startY: 70,
    head: [['Check Name', 'Status', 'Accepted']],
    body: preCalcTable.map(row => [row.checkName, row.status, row.accepted]),
    theme: 'grid',
    headStyles: { fillColor: [220, 220, 220] },
    styles: { fontSize: 10 }
  });
  doc.setFontSize(10);
  doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth - 80, 820);
  doc.addPage(); pageNum++;

  // --- 3 & 4. Model Setup + Applications & Aggregators (Grouped on one page) ---
  drawHeader();

  // --- Model Setup Section ---
  doc.setFontSize(18);
  doc.setFont(undefined, 'bold');
  doc.text('MODEL SETUP', 40, 50);
  doc.setFont(undefined, 'normal');
  const modelSetup = getModelSetupParameters(project, calculation);
  autoTable(doc, {
    startY: 70,
    head: [['Parameter', 'Value']],
    body: modelSetup.map(row => [row.label, row.value]),
    theme: 'grid',
    headStyles: { fillColor: [220, 220, 220] },
    styles: { fontSize: 10 }
  });

  // --- Applications Section ---
  const applicationsStartY = doc.lastAutoTable.finalY + 30;
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.text('Applications', 40, applicationsStartY);
  doc.setFont(undefined, 'normal');
  const applications = getApplications(project, calculation);
  autoTable(doc, {
    startY: applicationsStartY + 10,
    head: [['Application Name', 'Selected']],
    body: applications.map(app => [app.name, app.selected]),
    theme: 'grid',
    headStyles: { fillColor: [220, 220, 220] },
    styles: { fontSize: 10 }
  });

  // --- Aggregators Tab ---
  let extraY = doc.lastAutoTable.finalY + 30;
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.text('Aggregators Tab', 40, extraY);
  doc.setFont(undefined, 'normal');
  doc.setFontSize(12);
  doc.text(getAggregators(project, calculation), 60, extraY + 18);

  // --- Expected Basis Tab ---
  extraY += 48;
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.text('Expected Basis Tab', 40, extraY);
  doc.setFont(undefined, 'normal');
  doc.setFontSize(12);
  doc.text(getExpectedBasis(project, calculation, refDataBasis), 60, extraY + 18);

  // --- IBNR Tab ---
  extraY += 48;
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.text('IBNR Tab', 40, extraY);
  doc.setFont(undefined, 'normal');
  doc.setFontSize(12);
  // Get the job for the current officialJobId
  const officialJobId = project.officialJobId;
  const job = Array.isArray(calculation?.jobHistory)
    ? calculation.jobHistory.find(j => j.JobId === officialJobId)
    : null;
  const ibnrAsOf = job?.ObservationDate || '';
  doc.text(`As of Date: ${ibnrAsOf}`, 60, extraY + 18);

  doc.setFontSize(10);
  doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth - 80, 820);
  doc.addPage(); pageNum++;

  // --- 7.5. All Policies Imported ---
  drawHeader();
  doc.setFontSize(18);
  doc.setFont(undefined, 'bold');
  doc.text('ALL POLICIES IMPORTED', 40, 50);
  doc.setFont(undefined, 'normal');
  doc.setFontSize(14);
  doc.text('Policy Tracker', 40, 75);

  const allPoliciesImportedTable = getAllPoliciesImportedData(postCalc);
  autoTable(doc, {
    startY: 100,
    head: [['', 'Count', 'Percent']],
    body: allPoliciesImportedTable.length === 0
      ? [['No policy data found.', '', '']]
      : allPoliciesImportedTable.map(row => [
        row.policy_status,
        row.policy_count,
        row.percent
      ]),
    theme: 'grid',
    headStyles: { fillColor: [220, 220, 220] },
    styles: { fontSize: 10 }
  });
  doc.setFontSize(10);
  doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth - 80, 820);
  doc.addPage(); pageNum++;

  // --- 8. Peer Review ---
  drawHeader();
  doc.setFontSize(18);
  doc.setFont(undefined, 'bold');
  doc.text('PEER REVIEW', 40, 50);
  doc.setFont(undefined, 'normal');
  const peerReview = getPeerReviewData(project, review);
  doc.setFontSize(12);
  doc.text('Reviewers: ' + (peerReview.reviewers && peerReview.reviewers.length ? peerReview.reviewers.join(', ') : 'None'), 40, 80);
  doc.text('Status: ' + (peerReview.status || ''), 40, 100);
  let ySection = 120;
  peerReview.sections.forEach(section => {
    doc.setFont(undefined, 'bold');
    doc.text(section.title, 40, ySection);
    doc.setFont(undefined, 'normal');
    ySection += 16;
    if (section.comment) {
      doc.text('Comment: ' + section.comment, 60, ySection);
      ySection += 14;
    }
    if (section.extra) {
      doc.text(section.extra, 60, ySection);
      ySection += 14;
    }
    if (section.pii) {
      // Add prompt text
      doc.setFontSize(12);
      doc.text('Please review all the source columns for PII.', 60, ySection);
      ySection += 16;
      doc.text('Does the Project contain PII?', 60, ySection);
      ySection += 18;

      // Draw checkboxes and labels for Yes/No
      const boxSize = 12;
      const boxY = ySection - boxSize + 2;
      let x = 60;

      // Yes checkbox
      doc.rect(x, boxY, boxSize, boxSize);
      if (section.pii.yes) {
        doc.setLineWidth(2);
        doc.line(x + 3, boxY + boxSize / 2, x + boxSize / 2, boxY + boxSize - 3);
        doc.line(x + boxSize / 2, boxY + boxSize - 3, x + boxSize - 3, boxY + 3);
        doc.setLineWidth(1);
      }
      doc.text('Yes', x + boxSize + 6, ySection);

      // No checkbox
      x += 60;
      doc.rect(x, boxY, boxSize, boxSize);
      if (section.pii.no) {
        doc.setLineWidth(2);
        doc.line(x + 3, boxY + boxSize / 2, x + boxSize / 2, boxY + boxSize - 3);
        doc.line(x + boxSize / 2, boxY + boxSize - 3, x + boxSize - 3, boxY + 3);
        doc.setLineWidth(1);
      }
      doc.text('No', x + boxSize + 6, ySection);

      ySection += 18;
    }
  });
  doc.setFontSize(10);
  doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth - 80, 820);

  // --- Download Blob ---
  const pdfBytes = doc.output('arraybuffer');
  return new Blob([pdfBytes], { type: 'application/pdf' });
}

export {
  getColumnMappingData,
  getValueMappingData,
  getPreCalculationTableData,
  getModelSetupParameters,
  getAggregators,
  getApplications,
  getPeerReviewData,
  getExpectedBasis,
  getAllPoliciesImportedData,
  generateAuditReport
};
